// frontend-server/src/pages/VerifyEmailPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { validateEmail } from '../utils/validation';

const VerifyEmailPage = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP Verification
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [emailError, setEmailError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-fill email if passed from login page
  useEffect(() => {
    // Check URL params first
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    
    // Check location state (from login page)
    const stateEmail = location.state?.email;
    
    if (emailParam) {
      setEmail(emailParam);
      setStep(1); // Start with email confirmation
    } else if (stateEmail) {
      setEmail(stateEmail);
      setStep(1); // Start with email confirmation
    }
  }, [location]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (value) {
      const validation = validateEmail(value);
      setEmailError(validation.isValid ? '' : validation.error);
    } else {
      setEmailError('');
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
      }
    }
  };

  const handleSendVerificationOtp = async () => {
    if (!email || emailError) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email',
        text: emailError || 'Please enter a valid email address',
        confirmButtonColor: '#d33'
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(process.env.REACT_APP_API_BASE_URL + '/otp/send-verification-otp', { email });
      
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Verification Code Sent!',
          text: `A 6-digit verification code has been sent to ${email}`,
          confirmButtonColor: '#28a745'
        });
        setStep(2);
        setCountdown(120); // 2 minutes countdown
      }
    } catch (error) {
      console.error('Send verification OTP error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send verification code. Please try again.';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: '#d33'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid OTP',
        text: 'Please enter the complete 6-digit verification code',
        confirmButtonColor: '#d33'
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post( process.env.REACT_APP_API_BASE_URL + '/otp/verify-otp', {
        email,
        otp: otpCode,
        purpose: 'email_verification'
      });
      
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Email Verified Successfully!',
          text: 'Your email has been verified. You can now login without verification prompts.',
          confirmButtonColor: '#28a745'
        }).then(() => {
          navigate('/login', { 
            state: { 
              message: 'Email verified successfully! You can now login.',
              type: 'success' 
            }
          });
        });
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      const errorMessage = error.response?.data?.error || 'Invalid verification code. Please try again.';
      Swal.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: errorMessage,
        confirmButtonColor: '#d33'
      });
      
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      const firstInput = document.getElementById('otp-0');
      if (firstInput) firstInput.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    
    setIsResending(true);
    try {
      const response = await axios.post('/api/otp/send-verification-otp', { email });
      
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Code Resent!',
          text: 'A new verification code has been sent to your email',
          confirmButtonColor: '#28a745',
          timer: 2000
        });
        setCountdown(120); // Reset countdown
        setOtp(['', '', '', '', '', '']); // Clear current OTP
        const firstInput = document.getElementById('otp-0');
        if (firstInput) firstInput.focus();
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to resend verification code. Please try again.';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonColor: '#d33'
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleGoBack = () => {
    if (step === 2) {
      setStep(1);
      setOtp(['', '', '', '', '', '']);
      setCountdown(0);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-indigo-100">
            <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 1 
              ? 'Enter your email to receive a verification code'
              : 'Enter the 6-digit code sent to your email'
            }
          </p>
        </div>

        {step === 1 ? (
          <div className="mt-8 space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleEmailChange}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    emailError ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder="Enter your email address"
                />
                {emailError && (
                  <p className="mt-1 text-sm text-red-600">{emailError}</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleGoBack}
                className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back to Login
              </button>
              <button
                type="button"
                onClick={handleSendVerificationOtp}
                disabled={isLoading || !email || emailError}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </div>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                We've sent a 6-digit verification code to:
                <br />
                <span className="font-medium text-gray-900">{email}</span>
              </p>
              
              <div className="flex justify-center space-x-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-12 h-12 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    placeholder="0"
                  />
                ))}
              </div>
            </div>

            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-sm text-gray-500">
                  Resend code in {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending}
                  className="text-sm text-indigo-600 hover:text-indigo-500 font-medium disabled:opacity-50"
                >
                  {isResending ? 'Resending...' : 'Resend verification code'}
                </button>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleGoBack}
                className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Change Email
              </button>
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={isLoading || otp.join('').length !== 6}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verifying...
                  </div>
                ) : (
                  'Verify Email'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
